// The recheck surfaces — the trust loop (bead hf-e6-commitments-coverage-udt.5).
//
// Pure components over `recheckData.ts`. Copy from `@herefor/product-language`,
// components from `@herefor/design-system`, and no decision about permission here.
//
// ── THE ONE RULE THIS FILE EXISTS TO KEEP (plan §10.9) ─────────────────────────
//
// "A generic Acknowledge button is insufficient. The person must make a renewed
// commitment decision."
//
// So there is no Acknowledge, no Dismiss, no Got it, and no X in the corner. There
// are exactly two buttons and they are the same size: "Yes, I can still do this" and
// "No, I can't now". A surface with one prominent button and one quiet link would be
// an acknowledge button wearing a costume — it would tell somebody which answer the
// product wants, and that is the pressure this product refuses to apply.
//
// ── AND THE THING ABOVE THE BUTTONS ────────────────────────────────────────────
//
// A renewed decision needs something to decide about, so the diff is not optional
// decoration: every card shows WHAT moved, as "Was" and "Now", from the revision the
// person actually agreed to. When the server could not read that revision back, the
// card says so plainly rather than showing an empty table — and still asks.

import type { commitments as contracts } from "@herefor/contracts";
import { Button, Card, Dialog, EmptyState, Select, StatusMessage } from "@herefor/design-system";
import {
  ACTION_LABELS,
  PROXY_COMMITMENT_MESSAGES as PROXY_COPY,
  RECHECK_SURFACE_MESSAGES as COPY,
  RECONFIRMATION_MESSAGES as RECONFIRM,
} from "@herefor/product-language";
import { useState } from "react";

import type { DifferenceView, RecheckView } from "./recheckData";
import { hasNoReadableChanges } from "./recheckData";

/** What the caller is doing right now, so a card can disable itself once. */
export type RecheckState =
  | { readonly status: "idle" }
  | { readonly status: "sending"; readonly commitmentId: string }
  | { readonly status: "refused"; readonly commitmentId: string; readonly message: string };

export interface RecheckListProps {
  readonly rechecks: readonly RecheckView[];
  readonly state: RecheckState;
  readonly onAnswer: (recheck: RecheckView, decision: "yes_still_can" | "no_cannot") => void;
  /**
   * Disable both answers because the app cannot write right now (PLT-011, bead
   * hf-e6-commitments-coverage-udt.7).
   *
   * A seam rather than a store read: this file is a pure component and knows nothing
   * about connectivity, and one that imported a global would stop being testable
   * without one. `RecheckRoute` fills it, exactly as `NeedRoute` and `EventRoute`
   * fill their own `busy` from `useConnectivity()`. The NAME is theirs, deliberately:
   * one word for one idea across the app beats two that mean the same thing.
   *
   * Fail-closed and VISIBLE is the rule — an offline answer is not queued and not
   * pretended (PLT-011). A greyed-out pair of buttons is the honest state; a Yes that
   * appeared to work and vanished would be the worst outcome this flow can produce,
   * because the person would believe they had answered.
   */
  readonly actionsPaused?: boolean;
}

/**
 * A helper's outstanding rechecks, each with its own renewed decision.
 *
 * One card per question. They are NOT collapsed into a single "confirm all" —
 * agreeing to three changed arrangements with one tap is exactly the bulk re-consent
 * §10.9 forbids, and the fact that it would be more convenient is the point.
 */
export function RecheckList({
  rechecks,
  state,
  onAnswer,
  actionsPaused = false,
}: RecheckListProps) {
  return (
    <section aria-labelledby="recheck-list-heading" className="recheck-list">
      <h2 id="recheck-list-heading">{COPY.listTitle}</h2>

      {rechecks.length === 0 ? (
        <EmptyState description={COPY.noneOutstanding} title={RECONFIRM.title} />
      ) : (
        <ul className="recheck-list__items">
          {rechecks.map((recheck) => (
            <li key={recheck.commitmentId}>
              <RecheckCard
                actionsPaused={actionsPaused}
                onAnswer={onAnswer}
                recheck={recheck}
                state={state}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export interface RecheckCardProps {
  readonly recheck: RecheckView;
  readonly state: RecheckState;
  readonly onAnswer: (recheck: RecheckView, decision: "yes_still_can" | "no_cannot") => void;
  /** See `RecheckListProps.actionsPaused`. */
  readonly actionsPaused?: boolean;
}

export function RecheckCard({ recheck, state, onAnswer, actionsPaused = false }: RecheckCardProps) {
  // BOTH answers, together. Disabling only "Yes" would leave "No" as the one thing a
  // person could still do while offline — a nudge toward declining, produced by an
  // implementation detail rather than by anybody's intent.
  const busy =
    actionsPaused || (state.status === "sending" && state.commitmentId === recheck.commitmentId);
  const refusal =
    state.status === "refused" && state.commitmentId === recheck.commitmentId
      ? state.message
      : null;

  return (
    <Card title={recheck.label} titleLevel={3}>
      <p className="recheck-card__heading">{RECONFIRM.title}</p>
      <p className="recheck-card__introduction">{RECONFIRM.introduction}</p>

      {hasNoReadableChanges(recheck) ? (
        // Honest rather than reconstructed. The renewed decision is still asked,
        // because their agreement is still bound to a revision that has moved.
        <StatusMessage tone="info">{COPY.changeUnavailable}</StatusMessage>
      ) : (
        <DifferenceTable differences={recheck.differences} />
      )}

      {/* The window they are being asked about NOW, so the decision is made against
          a whole arrangement rather than a diff alone (NED-002, NED-010). */}
      <p className="recheck-card__window">
        {recheck.href === null ? recheck.label : <a href={recheck.href}>{recheck.label}</a>}
        <span className="recheck-card__time">{recheck.windowLabel}</span>
      </p>

      {refusal === null ? null : <StatusMessage tone="warning">{refusal}</StatusMessage>}

      {/* TWO ANSWERS, EQUALLY WEIGHTED. Same size, same row, no default. "No" is a
          secondary VARIANT rather than a smaller target because it must be exactly
          as easy to press — a person who genuinely cannot help must not have to work
          harder to say so than to say yes. */}
      <div className="recheck-card__answers">
        <Button
          disabled={busy}
          onClick={() => {
            onAnswer(recheck, "yes_still_can");
          }}
          variant="primary"
        >
          {RECONFIRM.confirm}
        </Button>
        <Button
          disabled={busy}
          onClick={() => {
            onAnswer(recheck, "no_cannot");
          }}
          variant="secondary"
        >
          {ACTION_LABELS.declineAfterChange}
        </Button>
      </div>
    </Card>
  );
}

/**
 * What moved, as "Was" and "Now".
 *
 * A definition list rather than a table: each row is one labelled fact with two
 * values, and table semantics would make a screen-reader user navigate a grid to
 * read three short sentences.
 */
export function DifferenceTable({
  differences,
}: {
  readonly differences: readonly DifferenceView[];
}) {
  return (
    <dl className="recheck-diff">
      {differences.map((difference) => (
        <div className="recheck-diff__row" data-field={difference.key} key={difference.key}>
          <dt>{difference.label}</dt>
          <dd>
            {difference.explanation === null ? (
              <>
                <span className="recheck-diff__was">
                  <span className="recheck-diff__label">{RECONFIRM.previousValueLabel}</span>
                  {/* A missing side is stated, never left blank: an empty cell beside
                      "Was" reads as data loss rather than as "there wasn't one". */}
                  <span>{difference.was ?? COPY.changeUnavailable}</span>
                </span>
                <span className="recheck-diff__now">
                  <span className="recheck-diff__label">{RECONFIRM.currentValueLabel}</span>
                  <span>{difference.now ?? COPY.changeUnavailable}</span>
                </span>
              </>
            ) : (
              <span className="recheck-diff__explanation">{difference.explanation}</span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* ------------------------------------------------------ the organiser's side --- */

/**
 * People, keyed by their id rather than their name.
 *
 * Two people in one circle can share a display name — the fixture family has two
 * Ninas — and a name-keyed list makes React drop one of them silently. It surfaced as
 * "Encountered two children with the same key, `Nina Alderton`" during the browser
 * suite, which is React telling us a real person had vanished from an organiser's
 * "still to answer" list. A person can also appear twice in `declined` legitimately,
 * by withdrawing, volunteering again and withdrawing again.
 */
export interface AudiencePerson {
  readonly personId: string;
  readonly displayName: string;
  /**
   * The open question, present on `awaiting` entries only (bead hf-ww3).
   *
   * Needed to answer on somebody's behalf: two people holding places on one need are
   * two separate questions, so the need's own id could not tell them apart.
   */
  readonly commitmentId?: string;
}

export interface RecheckAudience {
  readonly awaiting: readonly AudiencePerson[];
  readonly reconfirmed: readonly AudiencePerson[];
  readonly declined: readonly AudiencePerson[];
}

export interface ProxyAnswerChoice {
  readonly commitmentId: string;
  readonly displayName: string;
  readonly decision: "yes_still_can" | "no_cannot";
  readonly agreementSource: contracts.ProxyAgreementSource;
}

export interface RecheckAudiencePanelProps {
  readonly audience: RecheckAudience;
  /**
   * Server-decided: may this person record what somebody else said? Never inferred
   * here (AUT-007). Absent means no control at all, not a disabled one.
   */
  readonly canRecordProxyAnswers?: boolean;
  readonly onProxyAnswer?: (choice: ProxyAnswerChoice) => void;
  readonly actionsPaused?: boolean;
}

/**
 * Who the organiser is waiting on (plan §10.9, the other half of the loop).
 *
 * Three named lists. The person who made the change can see whether "Please recheck"
 * means one person still deciding or three who have not opened it — which is the
 * difference between knowing to wait and knowing to phone somebody.
 *
 * The headings describe the QUESTION's state, never the person's character: "Still to
 * answer", not "Has not responded". Somebody was asked a moment ago and is living
 * their life.
 */
export function RecheckAudiencePanel({
  audience,
  canRecordProxyAnswers = false,
  onProxyAnswer,
  actionsPaused = false,
}: RecheckAudiencePanelProps) {
  const total = audience.awaiting.length + audience.reconfirmed.length + audience.declined.length;
  if (total === 0) {
    return (
      <Card title={COPY.audienceTitle} titleLevel={3}>
        <p>{COPY.nobodyAffected}</p>
      </Card>
    );
  }

  return (
    <Card title={COPY.audienceTitle} titleLevel={3}>
      {audience.awaiting.length === 0 ? (
        // Deliberately NOT "everyone confirmed": some of them said no, and rounding
        // that up would be the product telling an organiser what they want to hear.
        <p className="recheck-audience__done">{COPY.everyoneAnswered}</p>
      ) : null}

      <PeopleList
        people={audience.awaiting}
        title={COPY.awaitingTitle}
        {...(canRecordProxyAnswers && onProxyAnswer !== undefined
          ? { onProxyAnswer, actionsPaused }
          : {})}
      />
      <PeopleList people={audience.reconfirmed} title={COPY.reconfirmedTitle} />
      <PeopleList people={audience.declined} title={COPY.declinedTitle} />
    </Card>
  );
}

function PeopleList({
  title,
  people,
  onProxyAnswer,
  actionsPaused = false,
}: {
  readonly title: string;
  readonly people: readonly AudiencePerson[];
  readonly onProxyAnswer?: (choice: ProxyAnswerChoice) => void;
  readonly actionsPaused?: boolean;
}) {
  if (people.length === 0) return null;

  return (
    <div className="recheck-audience__group">
      <h4 className="recheck-audience__title">{title}</h4>
      <ul className="recheck-audience__people">
        {people.map((person, index) => (
          // The id AND the position: one person can hold two commitments on the same
          // need over time (agreed, stepped back, agreed again, stepped back), so even
          // the id is not unique within a single list.
          <li key={`${person.personId}-${String(index)}`}>
            {person.displayName}
            {onProxyAnswer === undefined || person.commitmentId === undefined ? null : (
              <ProxyAnswer
                actionsPaused={actionsPaused}
                commitmentId={person.commitmentId}
                displayName={person.displayName}
                onAnswer={onProxyAnswer}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

const PROXY_SOURCES = Object.keys(PROXY_COPY.sources) as readonly contracts.ProxyAgreementSource[];

/**
 * Recording what somebody who is not in the app said about the change (bead hf-ww3).
 *
 * ONE PERSON, ONE ANSWER, AND THE SOURCE ASKED EVERY TIME. The source is not
 * remembered from their first agreement and is not defaulted past: they are being
 * asked about details they had not seen when they agreed, so the earlier conversation
 * cannot be the evidence for this one. The dialog says so out loud rather than
 * enforcing it silently.
 *
 * Yes and No are two buttons rather than a choice plus a submit, because there are
 * exactly two answers and neither is the safe default — an organiser who mis-taps a
 * single confirm button would record consent nobody gave.
 */
function ProxyAnswer({
  commitmentId,
  displayName,
  onAnswer,
  actionsPaused,
}: {
  readonly commitmentId: string;
  readonly displayName: string;
  readonly onAnswer: (choice: ProxyAnswerChoice) => void;
  readonly actionsPaused: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<contracts.ProxyAgreementSource>("phone");

  const answer = (decision: ProxyAnswerChoice["decision"]) => {
    setOpen(false);
    onAnswer({ commitmentId, displayName, decision, agreementSource: source });
  };

  return (
    <>
      <Button
        disabled={actionsPaused}
        onClick={() => {
          setOpen(true);
        }}
        variant="secondary"
      >
        {COPY.proxyAnswerAction(displayName)}
      </Button>

      <Dialog
        onClose={() => {
          setOpen(false);
        }}
        open={open}
        title={COPY.proxyAnswerTitle(displayName)}
        footer={
          <>
            <Button
              disabled={actionsPaused}
              onClick={() => {
                answer("yes_still_can");
              }}
              variant="primary"
            >
              {COPY.proxyAnswerYes}
            </Button>
            <Button
              disabled={actionsPaused}
              onClick={() => {
                answer("no_cannot");
              }}
              variant="secondary"
            >
              {COPY.proxyAnswerNo}
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
              }}
              variant="secondary"
            >
              {COPY.proxyAnswerCancel}
            </Button>
          </>
        }
      >
        <p>{COPY.proxyAnswerIntroduction}</p>
        <Select
          label={COPY.proxyAnswerSourceLabel}
          onChange={(event) => {
            setSource(event.target.value as contracts.ProxyAgreementSource);
          }}
          options={PROXY_SOURCES.map((value) => ({ value, label: PROXY_COPY.sources[value] }))}
          required
          value={source}
        />
        <p>{COPY.proxyAnswerConsequence(displayName, PROXY_COPY.sources[source])}</p>
      </Dialog>
    </>
  );
}
